'use client'

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Calendar, User } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import api from '../../lib/api.ts'
import placeholder from '../../assets/Car place holder PNG.png'

export default function LatestBlogs() {
  const { translate, language } = useLanguage()
  const isRTL = language === 'ar'
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchLatestBlogs = async () => {
      try {
        setLoading(true)
        const response = await api.get(
          '/blogs/paginated?page=1&limit=3&orderBy=id&order=DESC'
        )
        let blogData = []
        if (response.data && response.data.success) {
          if (response.data.data?.data) {
            blogData = response.data.data.data
          } else if (Array.isArray(response.data.data)) {
            blogData = response.data.data
          }
        }
        setBlogs(blogData)
      } catch (error) {
        console.error('Error fetching latest blogs:', error)
        setBlogs([])
      } finally {
        setLoading(false)
      }
    }

    fetchLatestBlogs()
  }, [])

  const getExcerpt = (htmlString) => {
    if (!htmlString) return ''
    const doc = new DOMParser().parseFromString(htmlString, 'text/html')
    return doc.body.textContent || doc.body.innerText || ''
  }

  if (loading) {
    return (
      <div className="py-20 text-center bg-[#020617]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary mx-auto"></div>
      </div>
    )
  }

  if (blogs.length === 0) return null

  return (
    <section className="py-24 bg-[#020617] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex flex-col md:flex-row justify-between items-end mb-12 gap-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
              {translate('blogPage.heading') || 'Latest News & Blogs'}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl">
              {translate('blogPage.subtitle') || 'Stay updated with the latest trends, tips, and news from the automotive world.'}
            </p>
          </div>
          <Link
            to="/blog"
            className="group flex items-center gap-2  hover:text-primary font-bold text-white transition-all duration-300 whitespace-nowrap"
          >
            {translate('common.viewAll') || 'View All Blogs'}
            <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <article
              key={blog.id}
              className="group flex flex-col bg-[#13191f] rounded-[2rem] overflow-hidden border border-white/5 hover:border-primary/30 transition-all duration-500 hover:-translate-y-2 shadow-2xl"
            >
              <Link to={`/blog/${blog.slug}`} className="relative h-64 overflow-hidden">
                <img
                  src={blog.featured_image || placeholder}
                  alt={blog.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#13191f] via-transparent to-transparent opacity-60"></div>

                {/* Category Badge if available - assuming blog.category or similar */}
                {blog.category && (
                  <div className="absolute top-6 left-6">
                    <span className="bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      {blog.category.name}
                    </span>
                  </div>
                )}
              </Link>

              <div className="p-8 flex flex-col flex-grow">
                <div className={`flex items-center gap-4 mb-6 text-xs text-gray-500 font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
                    <Calendar className="w-3.5 h-3.5 text-primary" />
                    <span>
                      {new Date(blog.createdAt).toLocaleDateString(
                        isRTL ? 'ar-AE' : 'en-US',
                        { month: 'long', day: 'numeric', year: 'numeric' }
                      )}
                    </span>
                  </div>
                  <span className="text-white/10">|</span>
                  <div className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
                    <User className="w-3.5 h-3.5 text-primary" />
                    <span>{blog.createdBy || 'Admin'}</span>
                  </div>
                </div>

                <Link
                  to={`/blog/${blog.slug}`}
                  className={`text-2xl font-bold text-white mb-4 line-clamp-2 leading-tight hover:text-primary transition-colors duration-300 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  {blog.title}
                </Link>

                <p className={`text-gray-400 text-sm mb-8 line-clamp-3 leading-relaxed flex-grow ${isRTL ? 'text-right' : 'text-left'}`}>
                  {getExcerpt(blog.description)}
                </p>

                <div className={`mt-auto flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <Link
                    to={`/blog/${blog.slug}`}
                    className={`inline-flex items-center gap-2 text-sm font-bold text-white group/btn transition-colors hover:text-primary ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <span className="relative">
                      {translate('blogPage.readMore') || 'Read More'}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover/btn:w-full"></span>
                    </span>
                    <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isRTL ? 'rotate-180 group-hover/btn:-translate-x-1' : 'group-hover/btn:translate-x-1'}`} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
